/**
 * Componente de filtros para a view de Documentos
 * Inclui: Regional e Toggle de tipos de documentos
 */
import { useEffect } from 'react';
import { useDocumentosFilters, DEFAULT_DOC_TYPES } from '../hooks/useDocumentosFilters';
import { useDocumentTypes, useRegionals } from '../hooks/useDocumentosData';
import { cn } from '@/lib/utils';
import { Filter, ChevronDown, RotateCcw, CheckSquare } from 'lucide-react';

// Cores para os botões toggle de cada tipo de documento
const DOC_TYPE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  'AVCB - Auto de Vistoria do Corpo de Bombeiros': { 
    bg: 'bg-red-500', border: 'border-red-500', text: 'text-white' 
  },
  'Alvará de Funcionamento': { 
    bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-white' 
  },
  'Licença Sanitária': { 
    bg: 'bg-green-500', border: 'border-green-500', text: 'text-white' 
  },
  'Licença Ambiental': { 
    bg: 'bg-emerald-600', border: 'border-emerald-600', text: 'text-white' 
  },
  'Licença Publicidade': { 
    bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-white' 
  },
};

function getDocTypeColors(docType: string) {
  return DOC_TYPE_COLORS[docType] || { bg: 'bg-gray-500', border: 'border-gray-500', text: 'text-white' };
}

// Abreviações para os tipos
function getDocTypeLabel(docType: string): string {
  if (docType.includes('AVCB')) return 'AVCB';
  if (docType.includes('Alvará')) return 'Alvará';
  if (docType.includes('Sanitária')) return 'Sanitária';
  if (docType.includes('Ambiental')) return 'Ambiental';
  if (docType.includes('Publicidade')) return 'Publicidade';
  return docType;
}

interface DocumentosFiltersProps {
  className?: string;
}

export function DocumentosFilters({ className }: DocumentosFiltersProps) {
  const { 
    regional, 
    activeDocTypes, 
    availableDocTypes,
    setRegional, 
    toggleDocType,
    setAvailableDocTypes,
    selectAllTypes,
    resetToDefaultTypes,
  } = useDocumentosFilters();

  // Buscar tipos disponíveis da API
  const { data: typesData } = useDocumentTypes();
  
  // Buscar regionais disponíveis da API
  const { data: regionaisData } = useRegionals();
  
  // Atualizar lista de tipos disponíveis quando a API responder
  useEffect(() => {
    if (typesData?.types) {
      setAvailableDocTypes(typesData.types);
    }
  }, [typesData, setAvailableDocTypes]);

  // Usar default se não houver dados da API ainda
  const displayTypes = availableDocTypes.length > 0 ? availableDocTypes : DEFAULT_DOC_TYPES;
  
  // Lista de regionais da API
  const regionais = regionaisData || [];

  return (
    <div className={cn('space-y-4', className)}>
      {/* Linha 1: Regional */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">Filtros:</span>
        </div>

        {/* Select de Regional */}
        <div className="relative">
          <select
            id="regional-filter-documentos"
            value={regional || ''}
            onChange={(e) => setRegional(e.target.value || null)}
            className={cn(
              'h-9 pl-3 pr-8 text-sm rounded-md border bg-background text-foreground appearance-none',
              'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
              'transition-colors',
              '[&>option]:bg-background [&>option]:text-foreground',
              regional && 'border-primary/50 bg-primary/5'
            )}
          >
            <option value="">Todas as Regionais</option>
            {regionais.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Linha 2: Toggle de tipos de documentos */}
      <div className="p-3 rounded-lg bg-muted/30 border">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Tipos de Documentos:</span>
            <span className="text-xs text-muted-foreground">(clique para ativar/desativar)</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={selectAllTypes}
              className={cn(
                'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border',
                'bg-background hover:bg-muted transition-colors',
                activeDocTypes.length === displayTypes.length && 'opacity-50 cursor-not-allowed'
              )}
              disabled={activeDocTypes.length === displayTypes.length}
              title="Selecionar todos os tipos"
            >
              <CheckSquare className="w-3 h-3" />
              Todos
            </button>
            <button
              onClick={resetToDefaultTypes}
              className={cn(
                'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border',
                'bg-background hover:bg-muted transition-colors'
              )}
              title="Resetar para seleção inicial (AVCB, Alvará, Sanitária, Ambiental, Publicidade)"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {displayTypes.map((docType) => {
            const isActive = activeDocTypes.includes(docType);
            const colors = getDocTypeColors(docType);
            
            return (
              <button
                key={docType}
                onClick={() => toggleDocType(docType)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-md border-2 transition-all duration-200',
                  isActive
                    ? cn(colors.bg, colors.border, colors.text, 'opacity-100')
                    : cn('bg-transparent', colors.border, 'text-muted-foreground', 'opacity-30 grayscale-[50%]'),
                  'hover:scale-105 hover:shadow-md'
                )}
                title={docType}
              >
                {getDocTypeLabel(docType)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
